<script lang="ts" setup>
import formatWork from '~/utils/formatWorksBy'
import { getPersonBySlug } from '~/queries/composer'

const { slug } = useResourceSlug()
const { locale } = useI18n()
const { data, pending, error } = await useAsyncData(
  `/composer/${slug}/works`,
  () =>
    execQuery(() => getPersonBySlug('works'), {
      slug,
    })
)

const composer = computed(() => {
  if (pending.value || error.value) return null
  return data.value.data.personBySlug
})

const title = computed(() => {
  return `Oeuvres par genre - ${composer.value?.firstName} ${composer.value?.lastName}`
})

const description = computed(() => {
  return `La liste des oeuvres par genre du compositeur ${composer.value?.firstName} ${composer.value?.lastName}`
})

useHead({
  title,
  meta: [
    {
      name: 'description',
      content: description,
    },
  ],
})

useSeoMeta({
  title,
  ogTitle: title,
  description,
  ogDescription: description,
  ogImage: composer.value?.photo?.getUrl,
  twitterCard: 'summary',
})

const kindOrder = [
  'Musique soliste',
  'Musique de chambre',
  "Musique instrumentale d'ensemble",
  'Musique concertante',
  'Musique vocale et instrument(s)',
  'Musique vocale a cappella',
  'Musique électronique / sur support / instruments mécaniques',
  'Effectif non spécifié',
]
// Group by Kind
const worksByKind: WorksByKind[] = []
composer.value.works.forEach((work: Work) => {
  let kind = work.style.split('(')[0].trim()
  let kindEn = work.styleEn.split('(')[0].trim()
  if (kind.startsWith('Musique vocale et instrument')) {
    kind = 'Musique vocale et instrument(s)'
    kindEn = 'Vocal music and instrument(s)'
  } else if (kind === 'Musique soliste') {
    kind = 'Musique soliste (sauf voix)'
    kindEn = 'Solo (excluding voice)'
  }
  const kindIndex = worksByKind.findIndex((group) => group.kind === kind)
  if (kindIndex === -1) {
    worksByKind.push({ kind, kindEn, works: [formatWork(work)] })
  } else {
    worksByKind[kindIndex].works.push(formatWork(work))
  }
  return work
})

worksByKind.sort(
  (a, b) => kindOrder.indexOf(a.kind) - kindOrder.indexOf(b.kind)
)
</script>
<template>
  <DetailComposer
    :resume="
      locale === 'fr'
        ? composer?.resume
        : composer?.resumeEn ?? composer?.resume
    "
    :composer="composer"
    :active-outline-id="'list_of_work_by_kind'"
  >
    <template #typeIcon>
      <div
        v-if="composer?.photo?.getUrl"
        class="hidden h-40 w-40 shrink-0 items-center justify-center bg-white sm:flex lg:h-60 lg:w-60"
      >
        <div class="relative">
          <img
            :src="composer?.photo?.getUrl"
            :alt="'works of ' + composer?.firstName + ' ' + composer?.lastName"
            class="h-full w-full object-cover sm:p-0"
          />
          <span
            v-if="composer?.copyrights"
            class="absolute bottom-0 right-1 text-white"
            >© {{ composer?.copyrights }}</span
          >
        </div>
      </div>
    </template>
    <template #prose>
      <div>
        <div class="pt-6 md:pt-0">
          <div v-for="item in worksByKind" :key="item.id" class="md:mt-4">
            <span class="text-primary-400 first-letter:uppercase">
              <span v-if="locale === 'fr'">{{ item.kind }}</span>
              <span v-else>{{ item.kindEn }}</span>
            </span>
            <hr class="my-1 border-t border-gray-300" />
            <div>
              <ListResource
                v-for="work in item.works.sort((a, b) =>
                  a.title.localeCompare(b.title)
                )"
                :key="work"
                :resource="work"
                :hide-icon="true"
                :minified="true"
              />
            </div>
          </div>
        </div>
      </div>

      <div v-html="composer?.catalogueSource" />
    </template>
  </DetailComposer>
</template>
